import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { VERIFIKATION_SERIES, type Account, type IsoDate, type Uuid, type Verifikation, type VerifikationMeta, type VerifikationSeries } from '../types';
import { getCompanySettings } from '../company-settings/service';
import { currentDeviceId } from '../devices/service';
import { periodKeyFor } from '../periods/keys';
import { latestLockedThrough } from '../periods/service';
import { withSharedLedger } from '../sync/shared-ledger';
import { reverseRows, validateVerifikation, type ValidateContext, type VerifikationDraft, type VerifikationRuleError } from './rules';

export const DEFAULT_SERIES: VerifikationSeries = 'A';

export class VerifikationRuleViolation extends Error {
  constructor(public readonly errors: VerifikationRuleError[]) {
    super(errors.map((e) => e.message).join('; '));
    this.name = 'VerifikationRuleViolation';
  }
}

export interface BookInput extends VerifikationDraft {
  companyId: Uuid;
  fiscalYearId: Uuid;
  /** 'A' (default), 'IB' opening balances, 'B' year-end. */
  series?: VerifikationSeries;
  correctsId?: Uuid;
  attachmentIds?: string[];
  /** Plain-language origin. Stored as-is and never changed afterwards. */
  meta?: VerifikationMeta;
}

/**
 * Whether a booking in `series` on `date` is exempt from the period lock:
 * opening balances on the fiscal year's first day and year-end entries on
 * its last day. Both are booked after the VAT periods of the year are
 * declared and neither touches a declared VAT period.
 */
export function isLockExempt(series: string, date: IsoDate, fy: { startDate: IsoDate; endDate: IsoDate }): boolean {
  if (series === 'IB') return date === fy.startDate;
  if (series === 'B') return date === fy.endDate;
  return false;
}

/**
 * Book a verifikation. Validates (including the latest period lock), assigns
 * the next gapless number for the (fiscalYear, series) pair, and appends.
 * This is the only write path; `meta` and `deviceId` are written here and
 * nowhere else.
 *
 * When the company's bookkeeping is shared between devices, the number is
 * only final once the remote copy has accepted it: the booking then runs
 * under `withSharedLedger` (pull, book, publish, commit). Called from inside
 * another flow's transaction it books plainly; that flow holds the lock.
 */
export async function bookVerifikation(input: BookInput, db: BookkeepingDB = defaultDb): Promise<Verifikation> {
  return withSharedLedger(input.companyId, db, () => bookLocally(input, db));
}

/** The local write: the transaction every path ends in. */
async function bookLocally(input: BookInput, db: BookkeepingDB): Promise<Verifikation> {
  const series: VerifikationSeries = input.series ?? DEFAULT_SERIES;
  const deviceId = currentDeviceId();
  return db.transaction('rw', [db.verifikationer, db.fiscalYears, db.accounts, db.periodLocks], async () => {
    const fiscalYear = await db.fiscalYears.get(input.fiscalYearId);
    if (!fiscalYear || fiscalYear.companyId !== input.companyId) {
      throw new VerifikationRuleViolation([{ code: 'outside_fiscal_year', message: 'Räkenskapsåret finns inte' }]);
    }
    const accounts = await db.accounts.where('companyId').equals(input.companyId).toArray();
    const activeAccounts = new Map<number, Account>(accounts.filter((a) => a.active).map((a) => [a.number, a]));

    const lockedThrough = latestLockedThrough(await db.periodLocks.where('companyId').equals(input.companyId).toArray());
    const ctx: ValidateContext = { ibSeries: series === 'IB' };
    if (lockedThrough !== undefined && !isLockExempt(series, input.date, fiscalYear)) ctx.lockedThrough = lockedThrough;

    const result = validateVerifikation(input, fiscalYear, activeAccounts, ctx);
    if (!result.ok) throw new VerifikationRuleViolation(result.errors);

    if (input.correctsId) {
      const original = await db.verifikationer.get(input.correctsId);
      if (!original || original.companyId !== input.companyId) {
        throw new VerifikationRuleViolation([{ code: 'missing_description', message: 'Verifikationen som ska rättas finns inte' }]);
      }
    }

    const last = await db.verifikationer
      .where('[fiscalYearId+series+number]')
      .between([input.fiscalYearId, series, 0], [input.fiscalYearId, series, Number.MAX_SAFE_INTEGER])
      .last();
    const number = (last?.number ?? 0) + 1;

    const verifikation: Verifikation = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      fiscalYearId: input.fiscalYearId,
      series,
      number,
      date: input.date,
      description: input.description.trim(),
      rows: input.rows.map((r) => ({ ...r })),
      attachmentIds: [...(input.attachmentIds ?? [])],
      bookedAt: new Date().toISOString(),
      /*
       * Provenance of the write, alongside `bookedAt`: which installation
       * entered this. Read from the in-memory identity, so the booking
       * transaction stays within the table scope its callers opened and a
       * device that was never established simply leaves it out.
       */
      ...(deviceId ? { deviceId } : {}),
      ...(input.correctsId ? { correctsId: input.correctsId } : {}),
      ...(input.meta ? { meta: structuredClone(input.meta) } : {}),
    };
    await db.verifikationer.add(verifikation);
    return verifikation;
  });
}

/**
 * The plain-language origin a reversal carries (pure). The reversal keeps the
 * original's kind, category, payment source, VAT and counterparty so reports
 * can group it with the original, but:
 *   - it is a manual correction (`source: 'manual'`, no reason/confidence);
 *   - it belongs to the VAT period of its own date, which may be a later,
 *     open period when the original's period is locked;
 *   - it does not settle anything and is not "the" booking of an inbox item
 *     or bank row, so those references are dropped (otherwise the
 *     `meta.inboxItemId` index would return two bookings for one item and a
 *     reversed settlement would look like a second settlement).
 */
export function reversalMeta(original: VerifikationMeta, periodKey: string): VerifikationMeta {
  const { inboxItemId: _inbox, bankRowIds: _rows, settlesVerifikationId: _settles, confidence: _confidence, ...kept } = structuredClone(original);
  return { ...kept, source: 'manual', reason: null, periodKey };
}

/**
 * Book a full reversal of an existing verifikation, dated `date`. The
 * reversal keeps the original's series and, when the original has one, a
 * corrected plain-language origin (see `reversalMeta`). Like every booking
 * it must be dated after the latest period lock.
 */
export async function reverseVerifikation(
  input: { originalId: Uuid; date: IsoDate; description?: string },
  db: BookkeepingDB = defaultDb,
): Promise<Verifikation> {
  const original = await db.verifikationer.get(input.originalId);
  if (!original) throw new Error('Verifikation not found');
  return withSharedLedger(original.companyId, db, () => reverseLocally(input, db));
}

async function reverseLocally(input: { originalId: Uuid; date: IsoDate; description?: string }, db: BookkeepingDB): Promise<Verifikation> {
  return db.transaction(
    'rw',
    [db.verifikationer, db.fiscalYears, db.accounts, db.periodLocks, db.companySettings, db.companies],
    async () => {
      const original = await db.verifikationer.get(input.originalId);
      if (!original) throw new Error('Verifikation not found');
      const fiscalYear = await db.fiscalYears.get(original.fiscalYearId);
      if (!fiscalYear) throw new Error('Fiscal year not found');
      const series = VERIFIKATION_SERIES.find((s) => s === original.series) ?? DEFAULT_SERIES;
      let meta: VerifikationMeta | undefined;
      if (original.meta) {
        const settings = await getCompanySettings(original.companyId, db);
        meta = reversalMeta(original.meta, periodKeyFor(input.date, fiscalYear, settings.vatPeriod));
      }
      return bookVerifikation(
        {
          companyId: original.companyId,
          fiscalYearId: fiscalYear.id,
          series,
          date: input.date,
          description: input.description ?? `Rättelse av ${original.series}${original.number}: ${original.description}`,
          rows: reverseRows(original.rows),
          correctsId: original.id,
          attachmentIds: [],
          ...(meta ? { meta } : {}),
        },
        db,
      );
    },
  );
}

export async function getVerifikation(id: Uuid, db: BookkeepingDB = defaultDb): Promise<Verifikation | undefined> {
  return db.verifikationer.get(id);
}

/** All verifikationer in a fiscal year, ordered by series then number. */
export async function listVerifikationer(fiscalYearId: Uuid, db: BookkeepingDB = defaultDb): Promise<Verifikation[]> {
  const list = await db.verifikationer.where('fiscalYearId').equals(fiscalYearId).toArray();
  return list.sort((a, b) => (a.series < b.series ? -1 : a.series > b.series ? 1 : a.number - b.number));
}

/** Verifikationer that correct the given one (its "Rättad av" chain). */
export async function correctionsOf(verifikationId: Uuid, db: BookkeepingDB = defaultDb): Promise<Verifikation[]> {
  return db.verifikationer.where('correctsId').equals(verifikationId).sortBy('bookedAt');
}
